import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import AddIcon from '@mui/icons-material/AddRounded';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlineRounded';
import CheckIcon from '@mui/icons-material/CheckRounded';
import CloseIcon from '@mui/icons-material/CloseRounded';
import AssignmentIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import ReplayIcon from '@mui/icons-material/ReplayRounded';
import moment from 'moment';
import 'moment/locale/fr';
import toast from 'react-hot-toast';

import { actionPlanApi } from '../../api/actionPlanApi';
import Scrollbar from '../../components/Scrollbar';
import {
  QUESTIONNAIRE_TYPES,
  formatDateRange,
  getTypeConfig
} from '../../components/dashboard/questionnaire/responses/constants';
import ArrowLeftIcon from '../../icons/ArrowLeft';
import { alpha } from '@mui/material/styles';
import { getTone, toneAlpha, grey, lightVeil, greyAlpha } from '../../theme/tones';

/**
 * Les colonnes du tableau métier. `width` est un pourcentage : combiné à
 * `tableLayout: fixed`, il fige la largeur des colonnes pour que les longues
 * phrases se replient sur 2, 3, 4 lignes au lieu d'étirer le tableau.
 */
const COLUMNS = [
  { key: 'theme', label: 'Thème', width: '11%' },
  { key: 'session', label: 'Session', width: '10%' },
  { key: 'improvement_point', label: 'Point à améliorer', width: '17%' },
  { key: 'action_taken', label: 'Action mise en place', width: '17%' },
  { key: 'action_situation', label: 'Situation Action', width: '15%' },
  { key: 'impact_followup', label: 'Suivi impact Action', width: '17%' },
  { key: 'closed_at', label: 'Action Clôturée', width: '13%' }
];

/** Champs de texte libre du formulaire, dans l'ordre du tableau. */
const TEXT_FIELDS = [
  { key: 'improvement_point', label: 'Point à améliorer', rows: 4 },
  { key: 'action_taken', label: 'Action mise en place', rows: 4 },
  { key: 'action_situation', label: 'Situation Action', rows: 3 },
  { key: 'impact_followup', label: 'Suivi impact Action', rows: 3 },
  { key: 'closed_at', label: 'Action Clôturée', rows: 1, placeholder: 'ex. 10 10 2026' }
];

/** Minuscules sans accents ni ponctuation : sert à comparer deux verbatims. */
const normalizeText = (value) => String(value || '')
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();

/**
 * Prochain numéro à employer dans « Point à améliorer ».
 *
 * Les cellules déjà saisies numérotent leurs points « 1/ », « 2/ » : une
 * proposition se range à la suite plutôt que de repartir à 1. Un texte non
 * numéroté compte pour le point 1, on enchaîne donc à 2.
 */
const nextNumber = (text) => {
  const value = String(text || '').trim();
  if (!value) return 1;

  const pattern = /(?:^|\n)\s*(\d+)\s*\//g;
  let max = 0;
  let match = pattern.exec(value);
  while (match) {
    max = Math.max(max, Number(match[1]));
    match = pattern.exec(value);
  }
  return max > 0 ? max + 1 : 2;
};

/** Verbatims que la cellule ne porte pas encore. */
const pendingVerbatims = (verbatims, existing) => {
  const haystack = normalizeText(existing);
  return (verbatims || []).filter((verbatim) => {
    const needle = normalizeText(verbatim);
    return needle !== '' && (haystack === '' || haystack.indexOf(needle) === -1);
  });
};

/** Bloc numéroté prêt à coller dans la colonne. */
const numberedBlock = (verbatims, start) => verbatims
  .map((verbatim, index) => `${start + index}/ ${verbatim}`)
  .join('\n');

const EMPTY_ROW = {
  theme: '',
  session_id: '',
  improvement_point: '',
  action_taken: '',
  action_situation: '',
  impact_followup: '',
  closed_at: ''
};

const sessionLabel = (session) => {
  if (!session) return '—';
  const date = session.start_date ? moment(session.start_date).format('DD/MM/YYYY') : null;
  return `N° ${session.number_session ?? '—'}${date ? ` · ${date}` : ''}`;
};

const ActionPlanFormation = () => {
  const { formationId } = useParams();
  const [searchParams] = useSearchParams();

  // Le type d'où l'on vient ne change pas le contenu du plan (il est porté par
  // la formation) : il sert à recolorer la page et à pré-remplir le thème.
  const type = searchParams.get('type') || 'Satisfaction';
  const typeConfig = getTypeConfig(type);
  const accent = typeConfig.accent;

  const [formation, setFormation] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // Ce que les apprenants ont écrit dans la question ouverte sur les axes
  // d'amélioration pédagogique : une proposition de remplissage, pas des
  // lignes du plan. Rien n'est enregistré sans un clic.
  const [suggestions, setSuggestions] = useState({ question: null, sessions: [], dismissed_count: 0 });
  // Propositions écartées. Le serveur les retire déjà de `suggestions` : cette
  // liste ne sert qu'à faire disparaître la ligne immédiatement, sans attendre
  // le rechargement. L'écart lui-même est enregistré en base, donc définitif.
  const [dismissed, setDismissed] = useState([]);
  const [dismissingKey, setDismissingKey] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [applyingKey, setApplyingKey] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_ROW);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async (signal) => {
    try {
      setLoading(true);
      const accessToken = window.localStorage.getItem('accessToken');
      const res = await actionPlanApi.getFormationPlan(formationId, accessToken, signal);
      if (res && res.status) {
        setFormation(res.formation || null);
        setSessions(res.sessions || []);
        setRows(res.rows || []);
        setSuggestions(res.suggestions || { question: null, sessions: [], dismissed_count: 0 });
      } else if (res?.message) {
        toast.error(res.message);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error(err);
        toast.error('Plan d’action indisponible');
      }
    } finally {
      setLoading(false);
    }
  }, [formationId]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const sessionsById = useMemo(() => {
    const map = new Map();
    sessions.forEach((s) => map.set(String(s.id), s));
    return map;
  }, [sessions]);

  // Une ligne peut viser une session absente de la liste (ses réponses ont été
  // supprimées depuis). Sans cette option de repêchage, le Select s'afficherait
  // vide et la session serait silencieusement perdue à l'enregistrement.
  const orphanSession = useMemo(() => {
    if (!form.session_id || sessionsById.has(String(form.session_id))) return null;
    const row = rows.find((r) => String(r.session_id) === String(form.session_id));
    return {
      id: form.session_id,
      number_session: row?.session_number ?? null,
      start_date: row?.session_start_date ?? null
    };
  }, [form.session_id, sessionsById, rows]);

  /**
   * Une proposition par session : les verbatims pas encore repris, numérotés à
   * la suite de ce que porte déjà la ligne. Les autres colonnes restent vides —
   * l'action mise en place s'écrit à la main.
   */
  const proposals = useMemo(() => (suggestions.sessions || [])
    .map((session) => {
      const existing = rows.find((row) => String(row.session_id) === String(session.session_id)) || null;
      const pending = pendingVerbatims(session.verbatims, existing?.improvement_point || '');
      if (!pending.length) return null;

      return {
        key: `proposal-${session.session_id}`,
        session,
        existing,
        text: numberedBlock(pending, nextNumber(existing?.improvement_point || '')),
        count: pending.length
      };
    })
    .filter(Boolean)
    .filter((proposal) => !dismissed.includes(proposal.key)),
  [suggestions, rows, dismissed]);

  /* --- formulaire --------------------------------------------------- */

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...EMPTY_ROW, theme: type });
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      theme: row.theme || '',
      session_id: row.session_id === null || row.session_id === undefined ? '' : String(row.session_id),
      improvement_point: row.improvement_point || '',
      action_taken: row.action_taken || '',
      action_situation: row.action_situation || '',
      impact_followup: row.impact_followup || '',
      closed_at: row.closed_at || ''
    });
    setDialogOpen(true);
  };

  const patchForm = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  /** Texte final d'une proposition : l'existant n'est jamais réécrit. */
  const mergedImprovement = (proposal) => {
    const existing = (proposal.existing?.improvement_point || '').replace(/\s+$/, '');
    return existing ? `${existing}\n${proposal.text}` : proposal.text;
  };

  /**
   * Écarte la proposition d'une session. L'écart est enregistré en base : la
   * proposition ne reviendra plus, ni au rafraîchissement, ni pour un collègue.
   * La ligne disparaît tout de suite, mais on recharge derrière pour remettre
   * le compteur « écartées » à jour.
   */
  const dismissProposal = async (proposal) => {
    const sessionId = proposal.session?.session_id;
    if (!sessionId) return;
    try {
      setDismissingKey(proposal.key);
      setDismissed((prev) => [...prev, proposal.key]);
      const accessToken = window.localStorage.getItem('accessToken');
      const res = await actionPlanApi.dismissSuggestion(formationId, sessionId, accessToken);
      if (res && res.status) {
        await load();
        setDismissed([]);
      } else {
        // L'écart n'a pas été enregistré : on remet la ligne, sinon l'utilisateur
        // croirait la décision prise alors qu'elle sera perdue au rechargement.
        setDismissed((prev) => prev.filter((key) => key !== proposal.key));
        toast.error(res?.message || 'Impossible d’écarter cette proposition');
      }
    } catch (err) {
      setDismissed((prev) => prev.filter((key) => key !== proposal.key));
      toast.error(err.message || 'Impossible d’écarter cette proposition');
    } finally {
      setDismissingKey(null);
    }
  };

  /** Remet à l'affichage tout ce qui a été écarté sur cette formation. */
  const restoreDismissed = async () => {
    try {
      setRestoring(true);
      const accessToken = window.localStorage.getItem('accessToken');
      const res = await actionPlanApi.restoreSuggestions(formationId, accessToken);
      if (res && res.status) {
        setDismissed([]);
        await load();
      } else {
        toast.error(res?.message || 'Impossible de réafficher les propositions');
      }
    } catch (err) {
      toast.error(err.message || 'Impossible de réafficher les propositions');
    } finally {
      setRestoring(false);
    }
  };

  /** Enregistre la proposition telle quelle : complète la ligne, ou la crée. */
  const applyProposal = async (proposal) => {
    try {
      setApplyingKey(proposal.key);
      const accessToken = window.localStorage.getItem('accessToken');
      const payload = proposal.existing
        ? {
          theme: proposal.existing.theme || type,
          session_id: proposal.existing.session_id === null ? '' : String(proposal.existing.session_id),
          improvement_point: mergedImprovement(proposal),
          action_taken: proposal.existing.action_taken || '',
          action_situation: proposal.existing.action_situation || '',
          impact_followup: proposal.existing.impact_followup || '',
          closed_at: proposal.existing.closed_at || ''
        }
        : {
          ...EMPTY_ROW,
          theme: type,
          session_id: String(proposal.session.session_id),
          improvement_point: proposal.text
        };

      const res = proposal.existing
        ? await actionPlanApi.updateRow(proposal.existing.id, payload, accessToken)
        : await actionPlanApi.createRow(formationId, payload, accessToken);

      if (res && res.status) {
        toast.success(proposal.existing ? 'Ligne complétée' : 'Ligne ajoutée');
        await load();
      } else {
        toast.error(res?.message || 'Enregistrement impossible');
      }
    } catch (err) {
      toast.error(err.message || 'Enregistrement impossible');
    } finally {
      setApplyingKey(null);
    }
  };

  /** Même contenu, mais dans le formulaire : on relit avant d'enregistrer. */
  const editProposal = (proposal) => {
    if (proposal.existing) {
      setEditingId(proposal.existing.id);
      setForm({
        theme: proposal.existing.theme || type,
        session_id: proposal.existing.session_id === null ? '' : String(proposal.existing.session_id),
        improvement_point: mergedImprovement(proposal),
        action_taken: proposal.existing.action_taken || '',
        action_situation: proposal.existing.action_situation || '',
        impact_followup: proposal.existing.impact_followup || '',
        closed_at: proposal.existing.closed_at || ''
      });
    } else {
      setEditingId(null);
      setForm({
        ...EMPTY_ROW,
        theme: type,
        session_id: String(proposal.session.session_id),
        improvement_point: proposal.text
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.theme) {
      toast.error('Le thème est requis');
      return;
    }
    try {
      setSaving(true);
      const accessToken = window.localStorage.getItem('accessToken');
      const res = editingId
        ? await actionPlanApi.updateRow(editingId, form, accessToken)
        : await actionPlanApi.createRow(formationId, form, accessToken);

      if (res && res.status) {
        toast.success(editingId ? 'Ligne modifiée' : 'Ligne ajoutée');
        setDialogOpen(false);
        await load();
      } else {
        toast.error(res?.message || 'Enregistrement impossible');
      }
    } catch (err) {
      toast.error(err.message || 'Enregistrement impossible');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Supprimer cette ligne du plan d’action ?')) return;
    try {
      setDeletingId(row.id);
      const accessToken = window.localStorage.getItem('accessToken');
      const res = await actionPlanApi.deleteRow(row.id, accessToken);
      if (res && res.status) {
        toast.success('Ligne supprimée');
        await load();
      } else {
        toast.error(res?.message || 'Suppression impossible');
      }
    } catch (err) {
      toast.error(err.message || 'Suppression impossible');
    } finally {
      setDeletingId(null);
    }
  };

  /* --- rendu -------------------------------------------------------- */

  const inputSx = {
    bgcolor: 'background.paper',
    '& .MuiOutlinedInput-root': { borderRadius: '12px' }
  };

  // Les cellules se replient sur plusieurs lignes plutôt que d'étirer la
  // colonne : c'est ce qui garde le tableau lisible sur les phrases longues.
  const cellSx = {
    fontSize: '0.78rem',
    color: 'text.primary',
    fontWeight: 600,
    verticalAlign: 'top',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    hyphens: 'auto',
    lineHeight: 1.45,
    borderColor: 'divider'
  };

  return (
    <>
      <Helmet>
        <title>Plan d’action | Qualité | Apteed</title>
      </Helmet>

      <Box sx={{ bgcolor: 'action.hover', minHeight: '100%', py: 3 }}>
        <Box sx={{ px: 2 }}>

          {/* ── En-tête ───────────────────────────────────────────── */}
          <Box
            sx={{
              mb: 2.5,
              p: 3,
              borderRadius: '24px',
              border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.9)}`,
              background: `linear-gradient(135deg, ${lightVeil(0.96)} 0%, ${greyAlpha(100, 0.75)} 100%)`,
              boxShadow: (theme) => `0 4px 24px ${alpha(theme.palette.common.black, 0.04)}`,
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              gap: 2.5
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, minWidth: 0 }}>
              <Box
                sx={{
                  width: 54, height: 54, borderRadius: '16px', flexShrink: 0,
                  background: typeConfig.gradient,
                  display: 'grid', placeItems: 'center',
                  boxShadow: `0 8px 20px ${accent}4D`
                }}
              >
                <AssignmentIcon sx={{ color: grey(0), fontSize: '1.6rem' }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '1.45rem', color: 'text.primary', letterSpacing: '-0.02em', lineHeight: 1.2 }}>
                  Plan d’action
                </Typography>
                <Typography sx={{ color: 'text.secondary', fontSize: '0.87rem', mt: 0.5 }}>
                  {loading && !formation ? 'Chargement…' : (formation?.name || 'Formation inconnue')}
                  {formation?.program_number ? ` · N° ${formation.program_number}` : ''}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
              {/* Cible explicitement le niveau sessions de la formation : un
                  navigate(-1) rouvrirait la page au niveau des formations,
                  puisque le drill-down est porté par l'URL. */}
              <Button
                component={RouterLink}
                to={`/dashboard/questionnaire-satisfaction?type=${encodeURIComponent(type)}&formation=${formationId}`}
                startIcon={<ArrowLeftIcon style={{ fontSize: 13 }} />}
                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 800, color: 'text.secondary' }}
              >
                Retour aux sessions
              </Button>
              <Button
                onClick={openCreate}
                variant="contained"
                startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                sx={{
                  borderRadius: '10px', textTransform: 'none', fontWeight: 800,
                  background: typeConfig.gradient, boxShadow: `0 4px 12px ${accent}4D`
                }}
              >
                Ajouter
              </Button>
            </Box>
          </Box>

          {/* Le plan est porté par la formation : le rappeler évite de croire
              qu'on tient trois plans distincts, un par type de questionnaire. */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <Chip
              label="Plan commun Pré-Formation · Post-Formation · Satisfaction"
              size="small"
              sx={{ bgcolor: `${accent}1A`, color: accent, fontWeight: 800, fontSize: '0.68rem', height: 22 }}
            />
            <Typography sx={{ fontSize: '0.78rem', color: 'text.disabled', fontWeight: 600 }}>
              {loading ? '' : `${rows.length} ligne${rows.length > 1 ? 's' : ''}`}
            </Typography>
            {!loading && proposals.length > 0 && (
              <Tooltip title={suggestions.question || ''}>
                <Chip
                  label={`${proposals.length} proposition${proposals.length > 1 ? 's' : ''} issue${proposals.length > 1 ? 's' : ''} des réponses`}
                  size="small"
                  sx={{
                    bgcolor: toneAlpha('amber', 0.16),
                    color: getTone('amber').text,
                    fontWeight: 800, fontSize: '0.68rem', height: 22
                  }}
                />
              </Tooltip>
            )}

            {/* Un écart est définitif : sans cette sortie, une proposition
                écartée par erreur serait introuvable. */}
            {!loading && (suggestions.dismissed_count || 0) > 0 && (
              <Button
                size="small"
                disabled={restoring}
                onClick={restoreDismissed}
                startIcon={restoring
                  ? <CircularProgress size={13} sx={{ color: 'text.disabled' }} />
                  : <ReplayIcon sx={{ fontSize: 15 }} />}
                sx={{
                  textTransform: 'none', fontWeight: 700, fontSize: '0.72rem',
                  color: 'text.disabled', minWidth: 0, px: 1
                }}
              >
                {`${suggestions.dismissed_count} écartée${suggestions.dismissed_count > 1 ? 's' : ''} · réafficher`}
              </Button>
            )}
          </Box>

          {/* ── Tableau ───────────────────────────────────────────── */}
          {loading && (
            <Box sx={{ display: 'grid', placeItems: 'center', py: 12 }}>
              <CircularProgress sx={{ color: accent }} />
            </Box>
          )}

          {!loading && rows.length === 0 && proposals.length === 0 && (
            <Card sx={{ py: 10, textAlign: 'center', borderRadius: '20px', border: `1px solid ${grey(100)}`, boxShadow: 'none' }}>
              <AssignmentIcon sx={{ fontSize: 44, color: grey(300), mb: 1.5 }} />
              <Typography sx={{ fontWeight: 800, color: 'text.secondary', mb: 0.5 }}>
                Aucune action pour cette formation
              </Typography>
              <Typography sx={{ fontSize: '0.85rem', color: 'text.disabled', mb: 2.5 }}>
                Ajoutez une première ligne pour tracer un point à améliorer et l’action mise en place.
              </Typography>
              <Button
                onClick={openCreate}
                startIcon={<AddIcon sx={{ fontSize: 17 }} />}
                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 800, color: accent }}
              >
                Ajouter une ligne
              </Button>
            </Card>
          )}

          {!loading && (rows.length > 0 || proposals.length > 0) && (
            <Card sx={{ borderRadius: '18px', border: `1px solid ${grey(100)}`, boxShadow: (theme) => `0 1px 3px ${alpha(theme.palette.common.black, 0.05)}`, overflow: 'hidden' }}>
              <Scrollbar>
                <Box sx={{ minWidth: 1100 }}>
                  <Table size="small" sx={{ tableLayout: 'fixed' }}>
                    <TableHead>
                      <TableRow>
                        {COLUMNS.map((col) => (
                          <TableCell
                            key={col.key}
                            align="center"
                            sx={{
                              width: col.width,
                              fontWeight: 800, color: 'text.secondary', fontSize: '0.68rem',
                              textTransform: 'uppercase', bgcolor: 'action.hover', borderColor: 'divider'
                            }}
                          >
                            {col.label}
                          </TableCell>
                        ))}
                        <TableCell
                          align="center"
                          sx={{
                            width: 92,
                            fontWeight: 800, color: 'text.secondary', fontSize: '0.68rem',
                            textTransform: 'uppercase', bgcolor: 'action.hover', borderColor: 'divider'
                          }}
                        >
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((row) => {
                        // La session vient de la ligne elle-même (jointe côté SQL)
                        // et non de la liste déroulante : celle-ci ne contient que
                        // les sessions ayant des réponses, alors qu'une ligne
                        // ancienne peut en viser une qui n'en a plus.
                        const hasSession = row.session_id !== null && row.session_id !== undefined;
                        const themeConfig = QUESTIONNAIRE_TYPES.find((t) => t.value === row.theme);

                        return (
                          <TableRow key={row.id} hover sx={{ '& td': { borderColor: 'divider' } }}>
                            <TableCell sx={{ ...cellSx, textAlign: 'center' }}>
                              <Chip
                                label={row.theme || '—'}
                                size="small"
                                sx={{
                                  bgcolor: themeConfig ? `${themeConfig.accent}1A` : toneAlpha('neutral', 0.12),
                                  color: themeConfig ? themeConfig.accent : getTone('neutral').color,
                                  fontWeight: 800,
                                  fontSize: '0.63rem',
                                  height: 'auto',
                                  py: 0.4,
                                  '& .MuiChip-label': { whiteSpace: 'normal', px: 1, lineHeight: 1.35 }
                                }}
                              />
                            </TableCell>

                            <TableCell sx={{ ...cellSx, textAlign: 'center' }}>
                              {hasSession ? (
                                <>
                                  <Typography sx={{ fontWeight: 800, fontSize: '0.78rem', color: 'text.primary' }}>
                                    N° {row.session_number ?? '—'}
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled', fontWeight: 700 }}>
                                    {formatDateRange(row.session_start_date, row.session_end_date)}
                                  </Typography>
                                </>
                              ) : (
                                <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled', fontWeight: 700 }}>
                                  Toutes
                                </Typography>
                              )}
                            </TableCell>

                            <TableCell sx={cellSx}>{row.improvement_point || '—'}</TableCell>
                            <TableCell sx={cellSx}>{row.action_taken || '—'}</TableCell>
                            <TableCell sx={cellSx}>{row.action_situation || '—'}</TableCell>
                            <TableCell sx={cellSx}>{row.impact_followup || '—'}</TableCell>
                            <TableCell sx={{ ...cellSx, textAlign: 'center', fontWeight: 800, color: row.closed_at ? getTone('emerald').base : grey(400) }}>
                              {row.closed_at || '—'}
                            </TableCell>

                            <TableCell sx={{ verticalAlign: 'top', whiteSpace: 'nowrap', textAlign: 'center' }}>
                              <Box sx={{ display: 'flex', gap: 0.25, justifyContent: 'center' }}>
                                <Tooltip title="Éditer">
                                  <IconButton size="small" onClick={() => openEdit(row)} sx={{ color: accent }}>
                                    <EditIcon sx={{ fontSize: 17 }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Supprimer">
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={deletingId === row.id}
                                      onClick={() => handleDelete(row)}
                                      sx={{ color: 'error.main' }}
                                    >
                                      <DeleteIcon sx={{ fontSize: 17 }} />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}

                      {/* Propositions : ce que les apprenants ont écrit, numéroté
                          à la suite de l'existant. Tant qu'on n'a pas cliqué,
                          rien n'est enregistré — et le texte déjà saisi dans la
                          ligne n'est jamais réécrit, seulement complété. */}
                      {proposals.map((proposal) => (
                        <TableRow
                          key={proposal.key}
                          sx={{
                            '& td': { borderColor: 'divider' },
                            bgcolor: toneAlpha('amber', 0.06)
                          }}
                        >
                          <TableCell sx={{ ...cellSx, textAlign: 'center' }}>
                            <Chip
                              label="Proposition"
                              size="small"
                              sx={{
                                bgcolor: toneAlpha('amber', 0.18),
                                color: getTone('amber').text,
                                fontWeight: 800, fontSize: '0.63rem', height: 20
                              }}
                            />
                            <Typography sx={{ fontSize: '0.66rem', color: 'text.disabled', fontWeight: 700, mt: 0.5 }}>
                              {proposal.existing ? 'à ajouter à la ligne' : 'nouvelle ligne'}
                            </Typography>
                          </TableCell>

                          <TableCell sx={{ ...cellSx, textAlign: 'center' }}>
                            <Typography sx={{ fontWeight: 800, fontSize: '0.78rem', color: 'text.primary' }}>
                              N° {proposal.session.session_number ?? '—'}
                            </Typography>
                            <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled', fontWeight: 700 }}>
                              {proposal.session.start_date
                                ? moment(proposal.session.start_date).format('DD/MM/YYYY')
                                : ''}
                            </Typography>
                          </TableCell>

                          <TableCell sx={cellSx}>{proposal.text}</TableCell>
                          <TableCell sx={cellSx} />
                          <TableCell sx={cellSx} />
                          <TableCell sx={cellSx} />
                          <TableCell sx={cellSx} />

                          <TableCell sx={{ verticalAlign: 'top', whiteSpace: 'nowrap', textAlign: 'center' }}>
                            <Box sx={{ display: 'flex', gap: 0.25, justifyContent: 'center' }}>
                              <Tooltip title={proposal.existing ? 'Compléter la ligne' : 'Enregistrer la ligne'}>
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={applyingKey === proposal.key}
                                    onClick={() => applyProposal(proposal)}
                                    sx={{ color: getTone('emerald').base }}
                                  >
                                    {applyingKey === proposal.key
                                      ? <CircularProgress size={15} sx={{ color: getTone('emerald').base }} />
                                      : <CheckIcon sx={{ fontSize: 18 }} />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Relire avant d’enregistrer">
                                <IconButton size="small" onClick={() => editProposal(proposal)} sx={{ color: accent }}>
                                  <EditIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Écarter définitivement de l’affichage">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={dismissingKey === proposal.key}
                                    onClick={() => dismissProposal(proposal)}
                                    sx={{ color: 'text.disabled' }}
                                  >
                                    {dismissingKey === proposal.key
                                      ? <CircularProgress size={15} sx={{ color: 'text.disabled' }} />
                                      : <CloseIcon sx={{ fontSize: 17 }} />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Scrollbar>
            </Card>
          )}
        </Box>
      </Box>

      {/* ── Formulaire ajout / édition ───────────────────────────── */}
      <Dialog
        open={dialogOpen}
        onClose={() => !saving && setDialogOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: '20px' } }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1.1rem', color: 'text.primary' }}>
          {editingId ? 'Modifier la ligne' : 'Ajouter une ligne'}
        </DialogTitle>
        <Divider sx={{ borderColor: 'divider' }} />
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <TextField
              select
              label="Thème"
              value={form.theme}
              onChange={(e) => patchForm({ theme: e.target.value })}
              sx={{ ...inputSx, flex: '1 1 240px' }}
            >
              {QUESTIONNAIRE_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Session"
              value={form.session_id}
              onChange={(e) => patchForm({ session_id: e.target.value })}
              sx={{ ...inputSx, flex: '1 1 240px' }}
            >
              <MenuItem value="">Toutes les sessions</MenuItem>
              {sessions.map((s) => (
                <MenuItem key={s.id} value={String(s.id)}>{sessionLabel(s)}</MenuItem>
              ))}
              {orphanSession && (
                <MenuItem value={String(orphanSession.id)}>
                  {`${sessionLabel(orphanSession)} · sans réponse`}
                </MenuItem>
              )}
            </TextField>
          </Box>

          {TEXT_FIELDS.map((field) => (
            <TextField
              key={field.key}
              label={field.label}
              placeholder={field.placeholder}
              value={form[field.key]}
              onChange={(e) => patchForm({ [field.key]: e.target.value })}
              multiline={field.rows > 1}
              minRows={field.rows > 1 ? field.rows : undefined}
              fullWidth
              sx={{ ...inputSx, mb: 2 }}
            />
          ))}
        </DialogContent>
        <Divider sx={{ borderColor: 'divider' }} />
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setDialogOpen(false)}
            disabled={saving}
            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 800, color: 'text.secondary' }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            variant="contained"
            sx={{
              borderRadius: '10px', textTransform: 'none', fontWeight: 800,
              background: typeConfig.gradient, boxShadow: `0 4px 12px ${accent}4D`
            }}
          >
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ActionPlanFormation;
